import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';

type ThemeMode = 'hell' | 'dunkel' | 'system';

interface DarkModeContextType {
  darkMode: boolean;
  themeMode: ThemeMode;
  toggleDarkMode: () => void;
  setThemeMode: (mode: ThemeMode) => void;
}

const DarkModeContext = createContext<DarkModeContextType | undefined>(undefined);

function getSystemPreference(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function getStorageKey(userId: number | null): string {
  return userId ? `themeMode_user_${userId}` : 'themeMode_guest';
}

function loadThemeMode(userId: number | null): ThemeMode {
  const key = getStorageKey(userId);
  const saved = localStorage.getItem(key);
  if (saved === 'hell' || saved === 'dunkel' || saved === 'system') {
    return saved;
  }
  return 'system';
}

function resolveDarkMode(mode: ThemeMode): boolean {
  if (mode === 'hell') return false;
  if (mode === 'dunkel') return true;
  return getSystemPreference();
}

export function DarkModeProvider({ children, userId }: { children: ReactNode; userId: number | null }) {
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => loadThemeMode(userId));
  const [darkMode, setDarkMode] = useState<boolean>(() => resolveDarkMode(loadThemeMode(userId)));

  // Reload preference when user changes (login/logout)
  useEffect(() => {
    const mode = loadThemeMode(userId);
    setThemeModeState(mode);
    setDarkMode(resolveDarkMode(mode));
  }, [userId]);

  // Listen to system preference changes when in "system" mode
  useEffect(() => {
    if (themeMode !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      setDarkMode(e.matches);
    };

    setDarkMode(mediaQuery.matches);

    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, [themeMode]);

  // Apply dark mode class
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Save preference per user
  useEffect(() => {
    const key = getStorageKey(userId);
    localStorage.setItem(key, themeMode);
  }, [themeMode, userId]);

  const setThemeMode = useCallback((mode: ThemeMode) => {
    setThemeModeState(mode);
    setDarkMode(resolveDarkMode(mode));
  }, []);

  const toggleDarkMode = useCallback(() => {
    setThemeMode(darkMode ? 'hell' : 'dunkel');
  }, [darkMode, setThemeMode]);

  return (
    <DarkModeContext.Provider value={{ darkMode, themeMode, toggleDarkMode, setThemeMode }}>
      {children}
    </DarkModeContext.Provider>
  );
}

export function useDarkMode() {
  const context = useContext(DarkModeContext);
  if (context === undefined) {
    throw new Error('useDarkMode must be used within a DarkModeProvider');
  }
  return context;
}
